// Folha inferior / bottom sheet (DESIGN §5.10). Fecha arrastando (> 80 px), no véu, com Esc ou no botão.
// role="dialog" aria-modal, foco preso e devolvido a quem abriu. No painel lg, prefira Modal ou Gaveta.
// `bloqueante` (envio-apple-estados §2, "Termos atualizados"): sem alça, sem "X", não fecha pelo véu, pelo Esc nem
// pelo voltar do Android; role="alertdialog", foco inicial no título e o resto do app `inert` enquanto aberta.
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cn } from "../lib/cn";
import { useFocoPreso, useTravarRolagem } from "../lib/hooks";
import { Icones } from "./icones";

export interface PropsSheet {
  aberto: boolean;
  /** Ignorado com `bloqueante` (a folha só sai quando quem a abriu deixa de renderizá-la). */
  aoFechar: () => void;
  titulo: ReactNode;
  /** Ações empilhadas no fim (primária em cima, w-full). */
  acoes?: ReactNode;
  /** Mostra botão "Fechar" no topo (conteúdo longo). */
  comFechar?: boolean;
  children?: ReactNode;
  className?: string;
  /** Folha que só sai pela ação dela (ver o cabeçalho). Usa `acoes` no fim, como a comum. */
  bloqueante?: boolean;
  /**
   * Passo dentro da mesma folha (ex.: o vídeo do exercício na prévia): seta "Voltar" à esquerda do título
   * (mockup treino-video-exercicio-app.html §1 e §4). `rotuloVoltar` é o nome acessível ("Voltar para a lista").
   */
  aoVoltar?: () => void;
  rotuloVoltar?: string;
  /** Linha de rótulo abaixo do título (o título trunca numa linha). */
  subtitulo?: ReactNode;
  /** "sobreTelaCheia": véu e folha em z-75, acima das telas cheias (z-70, ex.: descanso). */
  camada?: "padrao" | "sobreTelaCheia";
}

/** Raiz do React (main.tsx): fica `inert` atrás de uma folha bloqueante (a folha vive num portal no body). */
const ID_RAIZ = "raiz";

export function Sheet(props: PropsSheet) {
  return props.bloqueante ? <SheetBloqueante {...props} /> : <SheetComum {...props} />;
}

function SheetBloqueante({ aberto, titulo, acoes, children, className }: PropsSheet) {
  const ref = useRef<HTMLDivElement>(null);
  const idTitulo = useId();
  // Sem `aoFechar`: o Esc e o voltar do Android não fazem nada enquanto a folha estiver aberta.
  useFocoPreso(ref, aberto);
  useTravarRolagem(aberto);
  useEffect(() => {
    if (!aberto) return;
    const raiz = document.getElementById(ID_RAIZ);
    raiz?.setAttribute("inert", "");
    return () => raiz?.removeAttribute("inert");
  }, [aberto]);
  if (!aberto) return null;

  return createPortal(
    <>
      {/* z-80: acima de tudo, inclusive das telas cheias (z-70: leitor de QR, descanso), que ficam `inert` atrás. */}
      <div aria-hidden className="fixed inset-0 z-80 bg-veu animate-fade-entra" />
      <div
        ref={ref}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        className={cn(
          "fixed inset-x-0 bottom-0 z-80 mx-auto max-h-[90dvh] max-w-app overflow-y-auto",
          "rounded-t-folha bg-elevado px-margem pt-6 pb-[calc(16px+env(safe-area-inset-bottom,0px))] shadow-folha",
          "animate-folha-entra",
          className,
        )}
      >
        <h2 id={idTitulo} tabIndex={-1} data-autofoco className="text-h2 text-tinta focus:outline-none">
          {titulo}
        </h2>
        {children}
        {acoes}
      </div>
    </>,
    document.body,
  );
}

function SheetComum({
  aberto,
  aoFechar,
  titulo,
  acoes,
  comFechar,
  children,
  className,
  aoVoltar,
  rotuloVoltar,
  subtitulo,
  camada = "padrao",
}: PropsSheet) {
  const ref = useRef<HTMLDivElement>(null);
  const idTitulo = useId();
  const [arrasto, setArrasto] = useState(0);
  const inicioY = useRef<number | null>(null);
  const inicioT = useRef(0);

  useFocoPreso(ref, aberto, aoFechar);
  useTravarRolagem(aberto);
  if (!aberto) return null;

  const aoIniciar = (y: number) => {
    inicioY.current = y;
    inicioT.current = performance.now();
  };
  const aoMover = (y: number) => {
    if (inicioY.current === null) return;
    setArrasto(Math.max(0, y - inicioY.current));
  };
  const aoSoltar = () => {
    const velocidade = arrasto / Math.max(1, performance.now() - inicioT.current);
    inicioY.current = null;
    if (arrasto > 80 || velocidade > 0.8) aoFechar();
    setArrasto(0);
  };

  return createPortal(
    <>
      <div
        aria-hidden
        className={cn("fixed inset-0 bg-veu animate-fade-entra", camada === "sobreTelaCheia" ? "z-75" : "z-40")}
        onClick={aoFechar}
      />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        tabIndex={-1}
        className={cn(
          "fixed inset-x-0 bottom-0 mx-auto flex max-h-[90dvh] max-w-app flex-col",
          camada === "sobreTelaCheia" ? "z-75" : "z-50",
          "rounded-t-folha bg-painel pb-safe shadow-folha animate-folha-entra focus:outline-none",
          className,
        )}
        style={arrasto ? { transform: `translateY(${arrasto}px)`, transition: "none" } : undefined}
      >
        <div
          className="flex shrink-0 touch-none justify-center pt-2 pb-1"
          onPointerDown={(e) => aoIniciar(e.clientY)}
          onPointerMove={(e) => aoMover(e.clientY)}
          onPointerUp={aoSoltar}
          onPointerCancel={aoSoltar}
        >
          <span aria-hidden className="h-1.25 w-9 rounded-full bg-alca" />
        </div>
        <div
          className={cn(
            "flex justify-between gap-3 px-margem pt-2",
            aoVoltar || subtitulo ? "items-center" : "items-start",
          )}
        >
          {aoVoltar && (
            <button
              type="button"
              onClick={aoVoltar}
              aria-label={rotuloVoltar ?? "Voltar"}
              className="relative -mt-1 -ml-2 grid size-10 shrink-0 place-items-center rounded-controle text-texto-2 after:absolute after:-inset-1 after:content-[''] hover:bg-recuo"
            >
              <Icones.voltar size={20} aria-hidden />
            </button>
          )}
          {aoVoltar || subtitulo ? (
            <div className="min-w-0 flex-1">
              <h2 id={idTitulo} className="truncate text-h2 text-tinta">
                {titulo}
              </h2>
              {subtitulo && <p className="text-rotulo text-texto-3">{subtitulo}</p>}
            </div>
          ) : (
            <h2 id={idTitulo} className="text-h2 text-tinta">
              {titulo}
            </h2>
          )}
          {comFechar && (
            <button
              type="button"
              onClick={aoFechar}
              aria-label="Fechar"
              className="relative -mt-1 -mr-2 grid size-10 place-items-center rounded-controle text-texto-2 after:absolute after:-inset-1 after:content-[''] hover:bg-recuo"
            >
              <Icones.fechar size={20} aria-hidden />
            </button>
          )}
        </div>
        <div className="overflow-y-auto px-margem pt-3 pb-4 text-corpo text-texto-2">{children}</div>
        {acoes && <div className="flex shrink-0 flex-col gap-3 px-margem pt-1 pb-4">{acoes}</div>}
      </div>
    </>,
    document.body,
  );
}
